'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 감싸는 요소의 포커스 링을 **키보드 포커스일 때만** 켠다.
 *
 * ⚠️ 왜 CSS 로 안 되나 — `:focus-visible` 은 텍스트 입력에 대해서는 클릭으로 포커스해도
 * 항상 매칭된다(사양: 키보드 입력을 받는 요소는 포커스 방식과 무관하게 매칭. Chrome·Safari
 * 모두 그렇다). 그래서 `focus-within` 이든 `has-[:focus-visible]` 이든 검색창을 탭할 때마다
 * 링이 그려진다.
 *
 * ⚠️ "포커스 직전에 pointerdown 이 있었나" 로 판정하면 **터치에서 틀린다** — 터치는 포커스가
 * `pointerup` **뒤에** 들어와서, pointerup 에서 플래그를 내리는 순간 키보드로 오판한다
 * (마우스는 mousedown 에 포커스가 들어와 우연히 맞는다). 그래서 focus-visible 폴리필과 같은
 * 방식으로 **마지막 입력 수단**을 추적한다: keydown 이면 키보드, pointerdown 이면 포인터.
 */
export function useKeyboardFocusRing() {
  const [visible, setVisible] = useState(false);
  const hadKeyboardRef = useRef(false);

  useEffect(() => {
    const onKeyDown = () => {
      hadKeyboardRef.current = true;
    };
    const onPointerDown = () => {
      hadKeyboardRef.current = false;
    };

    // capture 로 받는다 — 중간에서 stopPropagation 하는 핸들러가 있어도 놓치지 않는다
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, []);

  // focusin/focusout 이라 안쪽 input 의 포커스도 래퍼에서 받는다
  const onFocus = useCallback(() => setVisible(hadKeyboardRef.current), []);
  const onBlur = useCallback(() => setVisible(false), []);

  return {
    /** 링 클래스를 붙일지 */
    visible,
    /** 링을 소유한 래퍼에 그대로 펼친다 */
    focusRingProps: { onFocus, onBlur },
  };
}
